import { useRef, useLayoutEffect, useEffect, useCallback } from 'react'
import { gsap } from 'gsap'

interface ChatTriggerAnimationRefs {
  buttonRef: React.RefObject<HTMLButtonElement | null>
  vineRef: React.RefObject<SVGPathElement | null>
  leafRef: React.RefObject<SVGPathElement | null>
  glowRef: React.RefObject<SVGCircleElement | null>
  hoverOverlayRef: React.RefObject<HTMLSpanElement | null>
}

interface UseChatTriggerAnimationOptions {
  isThinking: boolean
  hasNewMessage: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

/**
 * Encapsulates all GSAP animations for the ChatTrigger button:
 * - Mount: button pop-in → vine grows bottom-up → leaf unfurls
 * - Thinking: glow dot pulses from base to tip (repeat -1)
 * - New message: button bounce + leaf re-unfurl
 * - Hover: vine/leaf bend physics following cursor position
 * - Click: glow flash up the stem
 */
export const useChatTriggerAnimation = (
  refs: ChatTriggerAnimationRefs,
  { isThinking, hasNewMessage, onClick }: UseChatTriggerAnimationOptions
) => {
  const { buttonRef, vineRef, leafRef, glowRef, hoverOverlayRef } = refs
  const thinkingTlRef = useRef<gsap.core.Timeline | null>(null)

  // Mount: button pop-in → vine grows bottom-up → leaf unfurls
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set(buttonRef.current, { scale: 0.3, autoAlpha: 0 })
      gsap.set(vineRef.current, { scaleY: 0, svgOrigin: '16 32' })
      gsap.set(leafRef.current, { scale: 0, autoAlpha: 0, svgOrigin: '18 16' })

      gsap
        .timeline()
        .to(buttonRef.current, {
          scale: 1,
          autoAlpha: 1,
          duration: 0.35,
          ease: 'back.out(1.5)',
        })
        .to(vineRef.current, { scaleY: 1, duration: 0.5, ease: 'power2.out' })
        .to(
          leafRef.current,
          { scale: 1, autoAlpha: 1, duration: 0.45, ease: 'back.out(2)' },
          '>'
        )
    }, buttonRef)

    return () => ctx.revert()
  }, [buttonRef, vineRef, leafRef])

  // Thinking: glow dot pulses from base to tip
  useEffect(() => {
    if (isThinking) {
      thinkingTlRef.current = gsap
        .timeline({ repeat: -1 })
        .fromTo(
          glowRef.current,
          { attr: { cy: 28 }, autoAlpha: 0 },
          {
            attr: { cy: 4 },
            autoAlpha: 0.85,
            duration: 1,
            ease: 'power1.inOut',
          }
        )
        .to(glowRef.current, {
          autoAlpha: 0,
          duration: 0.35,
          ease: 'power1.in',
        })
        .set(glowRef.current, { attr: { cy: 28 } })
    } else {
      thinkingTlRef.current?.kill()
      thinkingTlRef.current = null
      gsap.set(glowRef.current, { autoAlpha: 0, attr: { cy: 28 } })
    }
  }, [isThinking, glowRef])

  // New message: bounce + leaf re-unfurl
  useEffect(() => {
    if (!hasNewMessage) return
    gsap
      .timeline()
      .to(buttonRef.current, { y: -5, duration: 0.18, ease: 'power2.out' })
      .to(buttonRef.current, { y: 0, duration: 0.35, ease: 'bounce.out' })
    gsap.fromTo(
      leafRef.current,
      { scale: 0, svgOrigin: '16 6' },
      {
        scale: 1,
        svgOrigin: '16 6',
        duration: 0.45,
        ease: 'back.out(2)',
        immediateRender: false,
      }
    )
  }, [hasNewMessage, buttonRef, leafRef])

  const handleMouseEnter = useCallback(() => {
    gsap.to(hoverOverlayRef.current, { autoAlpha: 1, duration: 0.2 })
  }, [hoverOverlayRef])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const btn = buttonRef.current
      if (!btn) return
      const rect = btn.getBoundingClientRect()
      const t = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2)
      // Compound bend: vine pivots from base, leaf bends further at its tip
      gsap.to(vineRef.current, {
        rotation: t * 9,
        svgOrigin: '16 32',
        duration: 0.22,
        overwrite: 'auto',
      })
      gsap.to(leafRef.current, {
        rotation: t * 5,
        x: t * 2,
        svgOrigin: '18 16',
        duration: 0.22,
        overwrite: 'auto',
      })
    },
    [buttonRef, vineRef, leafRef]
  )

  const handleMouseLeave = useCallback(() => {
    gsap.to(hoverOverlayRef.current, { autoAlpha: 0, duration: 0.25 })
    gsap.to([vineRef.current, leafRef.current], {
      rotation: 0,
      x: 0,
      duration: 0.4,
      ease: 'power2.out',
    })
  }, [hoverOverlayRef, vineRef, leafRef])

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (glowRef.current) {
        gsap.fromTo(
          glowRef.current,
          { attr: { cy: 28 }, autoAlpha: 0 },
          {
            attr: { cy: 4 },
            autoAlpha: 0.9,
            duration: 0.35,
            ease: 'power2.in',
            onComplete: () =>
              gsap.to(glowRef.current, { autoAlpha: 0, duration: 0.25 }),
          }
        )
      }
      onClick?.(e)
    },
    [glowRef, onClick]
  )

  return { handleMouseEnter, handleMouseMove, handleMouseLeave, handleClick }
}
